import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// Import translation resources
import heCommon from './resources/he/common.json';
import heAuth from './resources/he/auth.json';
import heDashboard from './resources/he/dashboard.json';
import heCourses from './resources/he/courses.json';
import heSessions from './resources/he/sessions.json';
import hePractice from './resources/he/practice.json';
import heMaterials from './resources/he/materials.json';
import heErrors from './resources/he/errors.json';
import heOnboarding from './resources/he/onboarding.json';
import heExams from './resources/he/exams.json';
import heCredits from './resources/he/credits.json';
import heAdmin from './resources/he/admin.json';
import heProfile from './resources/he/profile.json';
import heFeedback from './resources/he/feedback.json';

import enCommon from './resources/en/common.json';
import enAuth from './resources/en/auth.json';
import enDashboard from './resources/en/dashboard.json';
import enCourses from './resources/en/courses.json';
import enSessions from './resources/en/sessions.json';
import enPractice from './resources/en/practice.json';
import enMaterials from './resources/en/materials.json';
import enErrors from './resources/en/errors.json';
import enOnboarding from './resources/en/onboarding.json';
import enExams from './resources/en/exams.json';
import enCredits from './resources/en/credits.json';
import enAdmin from './resources/en/admin.json';
import enProfile from './resources/en/profile.json';
import enFeedback from './resources/en/feedback.json';

const resources = {
  he: {
    common: heCommon,
    auth: heAuth,
    dashboard: heDashboard,
    courses: heCourses,
    sessions: heSessions,
    practice: hePractice,
    materials: heMaterials,
    errors: heErrors,
    onboarding: heOnboarding,
    exams: heExams,
    credits: heCredits,
    admin: heAdmin,
    profile: heProfile,
    feedback: heFeedback,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    dashboard: enDashboard,
    courses: enCourses,
    sessions: enSessions,
    practice: enPractice,
    materials: enMaterials,
    errors: enErrors,
    onboarding: enOnboarding,
    exams: enExams,
    credits: enCredits,
    admin: enAdmin,
    profile: enProfile,
    feedback: enFeedback,
  },
};

const i18nConfig = {
  resources,
  lng: 'he', // Hebrew as default language
  fallbackLng: 'en', // English as fallback
  
  // Namespace configuration
  defaultNS: 'common',
  ns: ['common', 'auth', 'dashboard', 'courses', 'sessions', 'practice', 'materials', 'errors', 'onboarding', 'exams', 'credits', 'admin', 'profile', 'feedback'],
  
  interpolation: {
    escapeValue: false, // React already escapes values
  },
  
  // Language detection options
  detection: {
    order: process.env.NODE_ENV === 'development' 
      ? ['localStorage', 'navigator', 'htmlTag'] 
      : ['localStorage'], // Only use localStorage in production
    lookupLocalStorage: 'studybel-language',
    caches: ['localStorage'],
  },
  
  // Development options
  debug: process.env.NODE_ENV === 'development',
  
  react: {
    useSuspense: false, // We'll handle loading states manually
  },
};

i18n
  .use(initReactI18next);

// Only use language detection in development
if (process.env.NODE_ENV === 'development') {
  i18n.use(LanguageDetector);
}

i18n.init(i18nConfig);

export default i18n;

// Custom hooks for better developer experience
export const useNamespacedTranslation = (namespace: string) => {
  const { useTranslation } = require('react-i18next');
  const { t, i18n: i18nInstance } = useTranslation(namespace);
  
  return {
    t,
    i18n: i18nInstance,
    isRTL: i18nInstance.language === 'he',
    currentLanguage: i18nInstance.language,
  };
};

// Direction detection utility
export const getTextDirection = (language?: string): 'ltr' | 'rtl' => {
  const lang = language || i18n.language;
  return lang === 'he' ? 'rtl' : 'ltr';
};

// RTL languages list for future expansion
export const RTL_LANGUAGES = ['he', 'ar'];
export const isRTLLanguage = (language: string): boolean => {
  return RTL_LANGUAGES.includes(language);
}; 